import { useCallback, useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { getPendingExports, removePendingExport } from "./exportJobStorage";
import { ExportJobPoller } from "./ExportJobPoller";

/**
 * Global floating list of in-flight export jobs.
 *
 * Why this exists: ExportJobPoller lives inside the chat message that
 * triggered it. If the user closes the analytics panel or navigates to
 * another page, the Celery job keeps running but the UI loses the handle.
 * This widget reads the localStorage queue (written by ExportJobPoller on
 * mount) and revives every pending job's UI regardless of where the user
 * went — so they always have a single place to see "what's cooking" and
 * grab the download once it lands.
 *
 * Mount once at the AuthenticatedLayout level.
 */
export function PendingExportsWidget() {
  const [entries, setEntries] = useState(() => getPendingExports());
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  // Re-read localStorage on:
  //   - storage events (cross-tab sync)
  //   - a visibility-change (user came back to this tab)
  //   - an interval (belt-and-braces; handles same-tab writes from the
  //     inline ExportJobPoller, which localStorage `storage` events don't fire for).
  useEffect(() => {
    const refresh = () => setEntries(getPendingExports());
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === "rivmo_pending_exports") refresh();
    };
    const onVisibility = () => {
      if (!document.hidden) refresh();
    };
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisibility);
    const interval = window.setInterval(refresh, 3000);
    return () => {
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(interval);
    };
  }, []);

  const handleDownload = useCallback((url: string, filename: string) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  const dismiss = useCallback((jobId: string) => {
    removePendingExport(jobId);
    setDismissed((prev) => new Set(prev).add(jobId));
    setEntries((prev) => prev.filter((e) => e.jobId !== jobId));
  }, []);

  const visible = entries.filter((e) => !dismissed.has(e.jobId));
  if (visible.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-[320px] max-w-[calc(100vw-2rem)] flex-col gap-2"
      aria-live="polite"
      aria-label="Exports en curso"
    >
      {visible.map((entry) => (
        <div
          key={entry.jobId}
          // Explicit opaque zinc bg instead of bg-background. bg-background
          // resolves to hsl(var(--background)) which is technically opaque
          // but in contexts like this fixed overlay over maplibre, the
          // backdrop feels translucent because there's no parent scope
          // forcing it. Pinning a real color guarantees the card reads as
          // an independent surface on any map/page.
          className="pointer-events-auto rounded-lg border border-zinc-200 bg-white text-zinc-900 shadow-lg dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-50"
        >
          <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center gap-2 min-w-0">
              <Download className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate text-xs font-medium">
                {entry.layerName || "Reporte"}
                {entry.format ? ` · ${entry.format.toUpperCase()}` : ""}
              </span>
            </div>
            <button
              type="button"
              aria-label="Descartar"
              onClick={() => dismiss(entry.jobId)}
              className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="size-3" />
            </button>
          </div>
          <div className="p-2">
            <ExportJobPoller
              jobId={entry.jobId}
              statusUrl={entry.statusUrl}
              layerName={entry.layerName}
              format={entry.format}
              onDownloadReady={(url, filename) => {
                handleDownload(url, filename);
                // The poller itself removes the LS entry via stopPolling.
                setTimeout(() => dismiss(entry.jobId), 2000);
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
